import React, { useState, useEffect, useRef, useMemo } from "react";
import { Alert, Modal, Button, Spinner } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import { DatePicker } from "antd";
import RIcon from "../reservas/components/ReservasIcons";
import ClubOpenMatchesButton from "./components/ClubOpenMatchesButton";
import OpenMatchRulesStep from "./components/OpenMatchRulesStep";
import OpenMatchPublished from "./components/OpenMatchPublished";
import OpenMatchJoinForm from "./components/OpenMatchJoinForm";
import BuyerDataFields from "./components/BuyerDataFields";
import OpenMatchSummary from "./components/OpenMatchSummary";
import "./components/OpenMatchCompact.css";
import { wantsOpenMatchCreate } from "./openMatchesModel";
import MarketingOptIn from "../reservas/components/MarketingOptIn";
import { SucursalBanner, SucursalesSection } from "../reservas/components/Sucursales";
import { backTarget } from "../reservas/components/sucursalesModel";
import {
  SingleDatePickerPill,
  FALLBACK_IMAGE,
  WEEK_DAYS_ES,
  HoldCountdown,
  QtySelect,
  resolveUrl,
  resolveInstagramProfile,
} from "../reservas/components/ReservaShared";
import {
  SPORT_LABELS,
  SURFACE_LABELS,
  ENCLOSURE_LABELS,
  SERVICE_LABELS,
} from "../../constants/courtDomain";
import { useCourtReservationFlow } from "./useCourtReservationFlow";
import WaitlistJoin from "../listaDeEspera/WaitlistJoin";
import { isWaitlistEnabled } from "../listaDeEspera/waitlistModel";
import PlayersCountField from "./components/PlayersCountField";
import PerPlayerPrice from "./components/PerPlayerPrice";
import { buildMapQuery } from "./components/mapConfig";
import ReviewsSection from "../reservas/components/ReviewsSection";
import ClubBirthdaysSection from "./components/ClubBirthdaysSection";
import { visibleServices } from "../cumpleanos/birthdayModel";
import "../reservas/reservas-mobile.css";
import { resolveSlotDeposit, slotRequiresOnlinePayment } from "./courtDeposit";

const getSportIcon = () => {
  return "ball";
};

const getServiceIcon = (service) => {
  switch (service) {
    case "wifi":
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 12.5a13.3 13.3 0 0 1 14 0" />
          <path d="M8.5 16a7.7 7.7 0 0 1 7 0" />
          <path d="M2 9a18.5 18.5 0 0 1 20 0" />
          <line x1="12" y1="20" x2="12.01" y2="20" strokeWidth="3" />
        </svg>
      );
    case "vestuario":
      return <RIcon name="user" width={18} height={18} />;
    case "gimnasio":
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6.5 6.5h11M6.5 17.5h11M18 5v14M6 5v14M3 8v8M21 8v8" />
        </svg>
      );
    case "estacionamiento":
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="4" />
          <path d="M9 17V7h4a3 3 0 0 1 0 6H9" />
        </svg>
      );
    case "parrillas":
      return <RIcon name="spark" width={18} height={18} />;
    case "ayuda_medica":
      return <RIcon name="shield" width={18} height={18} />;
    case "bar":
    case "bar_restaurante":
      return <RIcon name="glass" width={18} height={18} />;
    case "torneos":
      return <RIcon name="star" width={18} height={18} />;
    case "cumpleanos":
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 12 20 22 4 22 4 12" />
          <rect x="2" y="7" width="20" height="5" />
          <line x1="12" y1="22" x2="12" y2="7" />
          <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
          <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
        </svg>
      );
    case "escuelita_deportiva":
      return <RIcon name="users" width={18} height={18} />;
    case "colegios":
      return <RIcon name="building" width={18} height={18} />;
    case "quincho":
      return <RIcon name="tent" width={18} height={18} />;
    case "disposicion_de_paletas":
      return <RIcon name="court" width={18} height={18} />;
    default:
      return <RIcon name="info" width={18} height={18} />;
  }
};

export default function ClubDetailMobile({ club, courts, sucursales = [], casaCentral = null, heroClub = null, sedes = null }) {
  // On a casa central the hero stays the casa's while `club` is the chosen sede.
  const hero = heroClub || club;
  const navigate = useNavigate();
  const services = visibleServices(club);
  const bookingsEnabled = club.onlineBookingEnabled === true;
  const location = useLocation();
  // `?crear=partido` (desde "Crear partido abierto") reutiliza este wizard para crear un partido:
  // Fecha > Cancha > Horario > Partido abierto > Tus datos > Publicar (sin hold ni pago en v1).
  const openMatchMode = wantsOpenMatchCreate(location?.search) && club.openMatches?.enabled === true;
  const flow = useCourtReservationFlow({ club, mode: openMatchMode ? "openMatch" : "reservation" });
  const calRef = useRef(null);

  const [step, setStep] = useState(() => {
    if (!bookingsEnabled) return "landing";
    if (flow.restoredStep === "hold" || flow.restoredStep === "matchData") return "datos";
    if (flow.restoredStep === "slots") return "horario";
    return "landing";
  });
  const [selectedTime, setSelectedTime] = useState(null);

  // Entrando por "Crear partido abierto" se salta la portada y se arranca en Fecha, una sola vez.
  const openMatchStartedRef = useRef(false);
  useEffect(() => {
    if (!openMatchMode || !bookingsEnabled || openMatchStartedRef.current) return;
    openMatchStartedRef.current = true;
    setStep("fecha");
    flow.requestAvailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openMatchMode, bookingsEnabled]);

  useEffect(() => {
    if (!bookingsEnabled) {
      setStep("landing");
      return;
    }

    if (flow.restoredStep === "hold" || flow.restoredStep === "matchData") {
      setStep("datos");
    } else if (flow.restoredStep === "slots") {
      setStep((current) =>
        current === "landing"
          ? (flow.selectedStart ? "cancha" : "horario")
          : current
      );
    }
  }, [
    flow.restoredStep,
    flow.selectedStart,
    bookingsEnabled
  ]);

  useEffect(() => {
    if (typeof window !== "undefined" && typeof window.scrollTo === "function" && process.env.NODE_ENV !== "test") {
      window.scrollTo(0, 0);
    }
  }, [step]);

  // Lock body overflow when in wizard steps (step !== "landing")
  useEffect(() => {
    if (step !== "landing" && bookingsEnabled) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, [step]);

  const {
    sports,
    selectedDate,
    setSelectedDate,
    selectedDuration,
    selectedSport,
    setSelectedSport,
    minDate,
    maxDate,
    requestAvailability,
    loadingAvail,
    availError,
    selectedCourtId,
    selectedStart,
    playersCount,
    setPlayersCount,
    playersEdited,
    setPlayersEdited,
    defaultPlayers,
    maxPlayers,
    selectSlot,
    holdExpiresAt,
    holdLoading,
    holdError,
    handleAdvanceToForm,
    handleHoldExpired,
    register,
    handleSubmit,
    errors,
    trigger,
    submitting,
    submitError,
    consent,
    setConsent,
    marketingOptIn,
    setMarketingOptIn,
    onSubmit,
    selectedCountry,
    setSelectedCountry,
    showPaymentModal,
    setShowPaymentModal,
    modalCard,
    setModalCard,
    cardOptions,
    selectedCardOption,
    setPendingValues,
    handleConfirmPaymentSelection,
  } = flow;

  const img = club.image || FALLBACK_IMAGE;
  const heroImg = hero.image || FALLBACK_IMAGE;
  const openingHours = Array.isArray(club.openingHours) ? club.openingHours : [];
  const todayIndex = dayjs().day();
  const instagramProfile = resolveInstagramProfile(club.instagram);

  const sportScopedCourts = useMemo(() => {
    const courtsList = Array.isArray(flow.availability?.courts) ? flow.availability.courts : [];
    return selectedSport
      ? courtsList.filter((court) => court.sport === selectedSport)
      : courtsList;
  }, [selectedSport, flow.availability?.courts]);

  const availableTimes = useMemo(() => {
    return Array.from(
      new Set(
        sportScopedCourts.flatMap((court) =>
          Object.values(court.bookableStarts || {}).flatMap((slots) =>
            Array.isArray(slots) ? slots.map((slot) => slot.slotStart) : []
          )
        )
      )
    ).sort((a, b) => a.localeCompare(b));
  }, [sportScopedCourts]);

  const courtsForSelectedTime = sportScopedCourts
    .map((court) => ({
      ...court,
      options: Object.entries(court.bookableStarts || {})
        .flatMap(([durationStr, slots]) =>
          (Array.isArray(slots) ? slots : [])
            .filter((slot) => slot.slotStart === selectedTime)
            .map((slot) => ({ duration: parseInt(durationStr, 10), ...slot }))
        )
        .sort((a, b) => a.duration - b.duration),
    }))
    .filter((court) => court.options.length > 0);

  const goBack = () => {
    if (step === "datos") return setStep(openMatchMode ? "partido" : "horario");
    if (step === "partido") return setStep("horario");
    if (step === "horario") return setStep("cancha");
    if (step === "cancha") return setStep("fecha");
    if (step === "fecha") return setStep("landing");
    navigate(backTarget("canchas", casaCentral).path);
  };

  const goToBooking = () => {
    setStep("fecha");
    requestAvailability();
    if (typeof window !== "undefined" && typeof window.scrollTo === "function" && process.env.NODE_ENV !== "test") {
      window.scrollTo(0, 0);
    }
  };

  const handleHoldExpiredLocal = () => {
    handleHoldExpired();
    setSelectedTime(null);
    setStep("horario");
  };

  const handleAdvanceToFormLocal = async () => {
    const result = await handleAdvanceToForm();
    if (result?.reused || result?.created) {
      setStep(openMatchMode ? "partido" : "datos");
    }
  };

  const onSubmitLocal = async (values) => {
    const result = await onSubmit(values);
    if (result === "slots") {
      setSelectedTime(null);
      setStep("horario");
    }
  };

  useEffect(() => {
    if (selectedStart?.slotStart) {
      setSelectedTime((current) => (current !== selectedStart.slotStart ? selectedStart.slotStart : current));
      return;
    }
    if (!selectedCourtId) {
      setSelectedTime((current) => (current && availableTimes.includes(current) ? current : null));
    }
  }, [selectedStart?.slotStart, selectedCourtId, availableTimes]);

  const selectedCourt = courts.find((c) => c._id === selectedCourtId || c.courtId === selectedCourtId);
  const displayPrice = selectedStart?.price ? `$${Number(selectedStart.price).toLocaleString("es-AR")}` : null;

  const availableSlotsForSelectedCourt = useMemo(() => {
    if (!selectedCourtId) return [];
    const court = sportScopedCourts.find((c) => (c._id || c.courtId) === selectedCourtId);
    if (!court) return [];
    const slots = court.bookableStarts?.[String(selectedDuration)] || [];
    return Array.isArray(slots) ? slots : [];
  }, [sportScopedCourts, selectedCourtId, selectedDuration]);

  const Header = ({ title, subtitle }) => (
    <div className="rmob-header">
      <button className="rmob-back" onClick={goBack} aria-label="Volver">
        <RIcon name="chevL" width={20} height={20} />
      </button>
      <div className="rmob-header-center">
        {title && <span className="rmob-header-title">{title}</span>}
        {subtitle && <span className="rmob-header-sub">{subtitle}</span>}
      </div>
      <div className="rmob-header-spacer" />
    </div>
  );

  const Progress = () => {
    let pct = 0;
    // En modo partido abierto hay un paso más ("partido"), así que la barra se reparte en 5.
    const stages = openMatchMode ? ["fecha", "cancha", "horario", "partido", "datos"] : ["fecha", "cancha", "horario", "datos"];
    const at = stages.indexOf(step);
    if (at >= 0) pct = ((at + 1) / stages.length) * 100;
    if (pct === 0) return null;
    return (
      <div className="rmob-progress">
        <div className="rmob-progress-bar" style={{ width: `${pct}%` }} />
      </div>
    );
  };

  const SummaryPill = () => {
    const parts = [];
    if (selectedDate) parts.push(selectedDate.format("DD/MM"));
    if (selectedCourt) parts.push(selectedCourt.name);
    if (selectedStart?.slotStart) parts.push(`${selectedStart.slotStart} hs`);
    else if (selectedTime) parts.push(`${selectedTime} hs`);
    if (!parts.length) return null;
    return <div className="rmob-summary-pill">{parts.join(" · ")}</div>;
  };

  const SportSelector = ({ resetTime = false }) =>
    sports.length > 1 ? (
      <div className="rmob-court-sports-grid" style={{ marginBottom: 16 }}>
        {sports.map((s) => (
          <button
            type="button"
            key={s}
            className={`court-grid-sport-btn-mobile${selectedSport === s ? " active" : ""}`}
            onClick={() => {
              setSelectedSport(s);
              if (resetTime) setSelectedTime(null);
              selectSlot(null, null, selectedDuration);
            }}
          >
            <RIcon name={getSportIcon(s)} width={14} height={14} />
            {SPORT_LABELS[s] || s}
          </button>
        ))}
      </div>
    ) : null;

  if (flow.published) {
    return (
      <div className="reservas-root rmob-root oma-mob-published">
        <Progress />
        <OpenMatchPublished info={flow.published} />
      </div>
    );
  }

  if (step === "landing" || !bookingsEnabled) {
    return (
      <div className="reservas-root rmob-root club-detail-mobile">
        <SucursalBanner kind="canchas" casaCentral={casaCentral} />
        <div className="rmob-landing-hero">
          <img src={heroImg} alt={hero.name} onError={(e) => { e.currentTarget.src = FALLBACK_IMAGE; }} />
          <div className="rmob-hero-scrim" />
          <button className="rmob-back rmob-back-hero" onClick={() => navigate(backTarget("canchas", casaCentral).path)} aria-label="Volver">
            <RIcon name="chevL" width={20} height={20} />
          </button>
          <div className="rmob-hero-info">
            <h1 className="rmob-hero-name">{hero.name}</h1>
            <div className="rmob-hero-meta">
              {hero.address && <span><RIcon name="pin" width={14} height={14} /> {hero.address}</span>}
            </div>
          </div>
        </div>

        <div className="rmob-landing-body">
          <SucursalesSection kind="canchas" sucursales={sucursales} id="sec-sucursales" sedes={sedes} />

          <div className="rmob-section">
            <h3 className="rmob-section-title">Descripción</h3>
            <p className="rmob-desc">{club.description || club.shortDescription || "Club disponible para reservas."}</p>
          </div>

          {bookingsEnabled && club.birthdays && (
            <ClubBirthdaysSection
              id="sec-cumpleanos"
              className="rmob-section"
              titleClassName="rmob-section-title"
              birthdays={club.birthdays}
              onReserve={() => navigate(`/canchas/${club._id}/cumpleanos`)}
            />
          )}

          <div className="rmob-section">
            <h3 className="rmob-section-title">Ubicación</h3>
            {club.address && (
              <p className="rmob-desc" style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "12px" }}>
                <RIcon name="pin" width={14} height={14} /> {club.address}
              </p>
            )}
            <div style={{ height: "200px", border: "1px solid #e5e7eb", borderRadius: "12px", overflow: "hidden", background: "#f3f4f6" }}>
              {(() => {
                const mapQuery = buildMapQuery(club);
                if (!mapQuery) return null;
                const mapSrc = `https://maps.google.com/maps?q=${encodeURIComponent(mapQuery)}&t=&z=15&ie=UTF8&iwloc=&output=embed`;
                return <iframe src={mapSrc} width="100%" height="100%" style={{ border: 0, display: "block" }} allowFullScreen="" loading="lazy" title="Google Maps" />;
              })()}
            </div>
          </div>

          {sports.length > 0 && (
            <div className="rmob-section">
              <h3 className="rmob-section-title">Deportes</h3>
              <div className="details-grid-premium">
                {sports.map((s) => (
                  <div className="detail-item-premium" key={s}>
                    <span className="icon"><RIcon name={getSportIcon(s)} width={18} height={18} /></span>
                    <div className="info">
                      <span className="label">Deporte</span>
                      <strong className="val">{SPORT_LABELS[s] || s}</strong>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="rmob-section">
            <h3 className="rmob-section-title">Canchas</h3>
            {courts.length === 0 ? (
              <p className="rmob-desc">Este club aún no cargó sus canchas.</p>
            ) : (
              <div className="rmob-contact-list">
                {courts.map((court) => (
                  <div className="rmob-contact-row" key={court._id || court.name}>
                    <RIcon name="ticket" width={16} height={16} />
                    <span>
                      {court.name} - {[
                        SPORT_LABELS[court.sport] || court.sport,
                        SURFACE_LABELS[court.surface] || court.surface,
                        ENCLOSURE_LABELS[court.enclosure] || court.enclosure,
                        court.lighting ? "Con iluminación" : "Sin iluminación",
                        court.courtCategory,
                      ].filter(Boolean).join(" · ")}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {openingHours.length > 0 && (
            <div className="rmob-section">
              <h3 className="rmob-section-title">Horarios de atención</h3>
              <div className="hours-list-premium">
                {openingHours.slice().sort((a, b) => a.weekday - b.weekday).map((h, i) => (
                  <div className={`hours-row-premium${h.weekday === todayIndex ? " today" : ""}${!h.enabled ? " closed" : ""}`} key={i}>
                    <span className="d">{WEEK_DAYS_ES[h.weekday]}</span>
                    <span className="h">{h.enabled ? `${h.open} – ${h.close}` : "Cerrado"}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(club.contactPhone || club.contactEmail || club.website || club.instagram) && (
            <div className="rmob-section">
              <h3 className="rmob-section-title">Contacto</h3>
              <div className="rmob-contact-list" style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {club.contactPhone && (
                  <div className="rmob-contact-row" style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", color: "var(--zoco-ink, #2d3035)" }}>
                    <RIcon name="phone" width={16} height={16} />
                    <span>{club.contactPhone}</span>
                  </div>
                )}
                {club.contactEmail && (
                  <div className="rmob-contact-row" style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", color: "var(--zoco-ink, #2d3035)" }}>
                    <RIcon name="mail" width={16} height={16} />
                    <span>{club.contactEmail}</span>
                  </div>
                )}
                {club.website && (
                  <div className="rmob-contact-row" style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", color: "var(--zoco-ink, #2d3035)" }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="2" y1="12" x2="22" y2="12" />
                      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                    </svg>
                    <a href={club.website.startsWith("http") ? club.website : `https://${club.website}`} target="_blank" rel="noopener noreferrer" style={{ color: "inherit", textDecoration: "underline" }}>
                      {club.website}
                    </a>
                  </div>
                )}
                {instagramProfile && (
                  <div className="rmob-contact-row" style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", color: "var(--zoco-ink, #2d3035)" }}>
                    <RIcon name="instagram" width={16} height={16} />
                    <a href={instagramProfile.href} target="_blank" rel="noopener noreferrer" style={{ color: "inherit", textDecoration: "underline" }}>
                      {instagramProfile.handle}
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}

          {services.length > 0 && (
            <div className="rmob-section">
              <h3 className="rmob-section-title">Servicios</h3>
              <div className="details-grid-premium">
                {services.map((s) => (
                  <div className="detail-item-premium" key={s}>
                    <span className="icon">{getServiceIcon(s)}</span>
                    <div className="info">
                      <span className="label">Servicio</span>
                      <strong className="val">{SERVICE_LABELS[s] || (s === "bar" ? "Bar" : s.charAt(0).toUpperCase() + s.slice(1))}</strong>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {Array.isArray(club.galleryimage) && club.galleryimage.length > 0 && (
            <div className="rmob-section" style={{ paddingRight: 0 }}>
              <h3 className="rmob-section-title">Galería</h3>
              <div className="rmob-gallery-carousel">
                {club.galleryimage.map((url, idx) => (
                  <div key={idx} className="rmob-gallery-item">
                    <img src={resolveUrl(url)} alt={`${club.name} foto ${idx + 1}`} onError={(e) => { e.currentTarget.src = FALLBACK_IMAGE; }} />
                  </div>
                ))}
              </div>
            </div>
          )}

          <ReviewsSection barId={club._id} isDesktop={false} />

          <div style={{ height: 100 }} />
        </div>

        
          <div className="rmob-cta-bar">
            <ClubOpenMatchesButton clubId={club._id} enabled={club.openMatches?.enabled === true} style={{ padding: 14, borderRadius: 14 }} />
            <button
              className="r-btn-primary rmob-cta-btn"
              onClick={goToBooking}
              disabled={!bookingsEnabled}
            >
              Reservar turno
              <RIcon
                name="arrowR"
                width={18}
                height={18}
              />
            </button>
          </div>
      </div>
    );
  }

  if (step === "fecha") {
    return (
      <div className="reservas-root rmob-root rmob-step-fixed">
        <Header title="Elegí la fecha" subtitle={club.name} />
        <Progress />
        <div className="rmob-step-body">
          <div className="rmob-date-quick">
            <button
              type="button"
              className={`rmob-date-chip${selectedDate.isSame(dayjs().startOf("day"), "day") ? " active" : ""}`}
              onClick={() => {
                const d = dayjs().startOf("day");
                setSelectedDate(d);
                setSelectedTime(null);
                selectSlot(null, null, selectedDuration);
              }}
            >Hoy</button>
            <button
              type="button"
              className={`rmob-date-chip${selectedDate.isSame(dayjs().add(1, "day").startOf("day"), "day") ? " active" : ""}`}
              onClick={() => {
                const d = dayjs().add(1, "day").startOf("day");
                setSelectedDate(d);
                setSelectedTime(null);
                selectSlot(null, null, selectedDuration);
              }}
            >Mañana</button>
          </div>

          <div className="rmob-calendar-wrap" ref={calRef}>
            <DatePicker
              value={selectedDate}
              onChange={(d) => {
                if (!d) return;
                setSelectedDate(d.startOf("day"));
                setSelectedTime(null);
                selectSlot(null, null, selectedDuration);
              }}
              disabledDate={(current) => current && (current.isBefore(minDate, "day") || current.isAfter(maxDate, "day"))}
              open={true}
              allowClear={false}
              getPopupContainer={() => calRef.current || document.body}
              style={{ display: "none" }}
              dropdownClassName="rmob-calendar-inline"
              popupClassName="rmob-calendar-inline"
            />
          </div>

          <div className="rmob-selected-date">
            <RIcon name="cal" width={16} height={16} />
            {selectedDate?.format("dddd DD [de] MMMM")}
          </div>

          {holdError && <Alert variant="danger" className="mt-3">{holdError}</Alert>}
        </div>
        <div className="rmob-cta-bar">
          <SummaryPill />
          <div className="rmob-cta-actions">
            <button type="button" className="r-btn-soft rmob-back-btn" onClick={goBack}>
              <RIcon name="chevL" width={16} height={16} /> Volver
            </button>
            <button className="r-btn-primary rmob-cta-btn" onClick={() => setStep("cancha")}>
              Continuar <RIcon name="arrowR" width={18} height={18} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === "cancha") {
    return (
      <div className="reservas-root rmob-root rmob-step-fixed">
        <Header title="Elegí cancha y duración" subtitle={club.name} />
        <Progress />
        <div className="rmob-step-body">
          <SportSelector resetTime={false} />
          <div className="rmob-selected-date" style={{ margin: "0 0 20px", display: "flex", alignItems: "center", gap: 8, fontSize: 15, fontWeight: 700, color: "var(--zoco-lime-strong, #8FA80A)" }}>
            <RIcon name="cal" width={18} height={18} />
            {selectedDate?.format("dddd DD [de] MMMM")}
          </div>

          {loadingAvail ? (
            <div style={{ minHeight: 160, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Spinner animation="border" style={{ color: "var(--zoco-lime-strong)" }} />
            </div>
          ) : availError ? (
            <Alert variant="danger">{availError}</Alert>
          ) : sportScopedCourts.length === 0 ? (
            <div style={{ padding: "30px 20px", textAlign: "center", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, fontSize: 14, color: "#6b7280" }}>
              No hay canchas disponibles para este deporte.
            </div>
          ) : (
            <div className="rmob-court-list-section" style={{ marginTop: 0 }}>
              {sportScopedCourts.map((court) => {
                const courtId = court._id || court.courtId;
                const availableDurations = Object.keys(court.bookableStarts || {}).map(Number).filter((d) => flow.durations.includes(d));
                const displayDurations = availableDurations.length ? availableDurations : flow.durations;

                return (
                  <div className="rmob-court-card" key={courtId}>
                    <div className="rmob-court-card-header">
                      <span className="rmob-court-card-name">{court.name}</span>
                      <span className="rmob-court-card-meta">
                        {[
                          SURFACE_LABELS[court.surface] || court.surface,
                          ENCLOSURE_LABELS[court.enclosure] || court.enclosure,
                        ].filter(Boolean).join(" · ")}
                      </span>
                    </div>
                    <div className="rmob-court-card-durations">
                      {displayDurations.map((duration) => {
                        const isPillSelected = selectedCourtId === courtId && selectedDuration === duration;
                        const samplePrice = court.bookableStarts?.[String(duration)]?.[0]?.price;

                        return (
                          <button
                            type="button"
                            key={`${courtId}-${duration}`}
                            className={`rmob-court-duration-pill${isPillSelected ? " selected" : ""}`}
                            onClick={() => {
                              selectSlot(courtId, null, duration);
                              setSelectedTime(null);
                            }}
                          >
                            {samplePrice != null && (
                              <span className="price">{`$${Number(samplePrice).toLocaleString("es-AR")}`}</span>
                            )}
                            <span className="duration">{duration} min</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <WaitlistJoin
            enabled={isWaitlistEnabled(club)}
            barId={club._id}
            kind="court"
            date={selectedDate?.format("YYYY-MM-DD")}
            criteria={{ ...(selectedSport ? { sport: selectedSport } : {}), durationMinutes: Number(selectedDuration) }}
            summary={[selectedSport, selectedDuration ? selectedDuration + " min" : ""].filter(Boolean).join(" · ")}
          />
          {holdError && <Alert variant="danger" className="mt-3">{holdError}</Alert>}
        </div>
        <div className="rmob-cta-bar">
          <SummaryPill />
          <div className="rmob-cta-actions">
            <button type="button" className="r-btn-soft rmob-back-btn" onClick={goBack}>
              <RIcon name="chevL" width={16} height={16} /> Volver
            </button>
            <button
              className="r-btn-primary rmob-cta-btn"
              disabled={!selectedCourtId}
              onClick={() => setStep("horario")}
              style={{ opacity: !selectedCourtId ? 0.5 : 1 }}
            >
              Continuar <RIcon name="arrowR" width={18} height={18} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === "horario") {
    return (
      <div className="reservas-root rmob-root rmob-step-fixed">
        <Header title="Elegí un horario" subtitle={club.name} />
        <Progress />
        <div className="rmob-step-body">
          <div style={{ marginBottom: 16, padding: "12px 16px", borderRadius: 12, background: "#f9fafb", border: "1px solid #e5e7eb" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, color: "#2d3035" }}>
              <RIcon name="court" width={16} height={16} />
              Cancha: {selectedCourt?.name || "-"} · {selectedDuration} min
            </div>
            <div style={{ marginTop: 6, fontSize: 13, color: "#6b7280" }}>
              {selectedDate?.format("dddd DD [de] MMMM")}
            </div>
          </div>

          {loadingAvail ? (
            <div style={{ minHeight: 160, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Spinner animation="border" style={{ color: "var(--zoco-lime-strong)" }} />
            </div>
          ) : availError ? (
            <Alert variant="danger">{availError}</Alert>
          ) : availableSlotsForSelectedCourt.length === 0 ? (
            <div style={{ padding: "30px 20px", textAlign: "center", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, fontSize: 14, color: "#6b7280" }}>
              No hay horarios disponibles para esta cancha en la fecha elegida. Probá con otra cancha o fecha.
            </div>
          ) : (
            <div className="rmob-court-hours-grid">
              {availableSlotsForSelectedCourt.map((slot) => {
                const isSelected = selectedStart?.slotStart === slot.slotStart;
                return (
                  <button
                    key={slot.slotStart}
                    type="button"
                    className={`rmob-court-hour-btn${isSelected ? " active" : ""}`}
                    onClick={() => {
                      selectSlot(selectedCourtId, slot, selectedDuration);
                      setSelectedTime(slot.slotStart);
                    }}
                  >
                    {slot.slotStart}
                  </button>
                );
              })}
            </div>
          )}
          {holdError && <Alert variant="danger" className="mt-3">{holdError}</Alert>}
          {flow.createError && <Alert variant="danger" className="mt-3">{flow.createError}</Alert>}
        </div>
        <div className="rmob-cta-bar">
          <SummaryPill />
          <div className="rmob-cta-actions">
            <button type="button" className="r-btn-soft rmob-back-btn" onClick={goBack}>
              <RIcon name="chevL" width={16} height={16} /> Volver
            </button>
            <button
              className="r-btn-primary rmob-cta-btn"
              disabled={holdLoading || !selectedStart}
              onClick={handleAdvanceToFormLocal}
              style={{ opacity: holdLoading || !selectedStart ? 0.5 : 1 }}
            >
              {holdLoading ? <Spinner animation="border" size="sm" /> : <>Continuar <RIcon name="arrowR" width={18} height={18} /></>}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === "partido" && openMatchMode) {
    return (
      <div className="reservas-root rmob-root rmob-step-fixed">
        <Header title="Partido abierto" subtitle={club.name} />
        <Progress />
        <div className="rmob-step-body oma-mob-body">
          <OpenMatchRulesStep
            compact
            capacity={flow.matchCapacity}
            rules={flow.matchRules}
            closeHours={club.openMatches?.closeHours}
            date={flow.dateLabel}
            slotStart={selectedStart?.slotStart}
            price={selectedStart?.price}
            onChange={flow.setMatchRules}
          />
        </div>
        <div className="rmob-cta-bar">
          <SummaryPill />
          <div className="rmob-cta-actions">
            <button type="button" className="r-btn-soft rmob-back-btn" onClick={goBack}>
              <RIcon name="chevL" width={16} height={16} /> Volver
            </button>
            <button
              className="r-btn-primary rmob-cta-btn"
              onClick={async () => {
                // Creating needs a session: a guest goes to /login and comes back to the data step.
                const result = await flow.continueOpenMatch();
                if (result?.ok) setStep("datos");
              }}
            >
              Continuar <RIcon name="arrowR" width={18} height={18} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === "datos" && openMatchMode) {
    return (
      <div className="reservas-root rmob-root rmob-step-fixed">
        <Header title="Tus datos" subtitle={club.name} />
        <Progress />
        <div className="rmob-step-body rmob-form-body oma-mob-body">
          <OpenMatchSummary
            variant="mobile"
            clubName={club.name}
            courtName={
              (selectedCourt ||
                (Array.isArray(flow.availability?.courts) ? flow.availability.courts : []).find(
                  (c) => c._id === selectedCourtId || c.courtId === selectedCourtId,
                ))?.name
            }
            date={selectedDate}
            slotStart={selectedStart?.slotStart}
            duration={selectedDuration}
            alreadyPlayers={flow.matchRules.alreadyPlayers}
            capacity={flow.matchCapacity}
            price={selectedStart?.price}
          />
          <OpenMatchJoinForm
            compact
            hideActions
            formId="open-match-form"
            title={null}
            clubName={club.name}
            defaults={flow.authUser}
            submitting={submitting}
            error={flow.createError}
            onCancel={goBack}
            onSubmit={async (values, country, optIn) => {
              const result = await flow.publishOpenMatch(values, country, optIn);
              if (result === "slots") {
                setSelectedTime(null);
                setStep("horario");
              }
            }}
            consentRules="cada jugador paga su parte en el club y el partido se cierra a la hora indicada."
          />
        </div>
        <div className="rmob-cta-bar">
          <SummaryPill />
          <div className="rmob-cta-actions">
            <button type="button" className="r-btn-soft rmob-back-btn" onClick={goBack}>
              <RIcon name="chevL" width={16} height={16} /> Volver
            </button>
            <button type="submit" form="open-match-form" className="r-btn-primary rmob-cta-btn" disabled={flow.submitting}>
              {flow.submitting ? "Publicando…" : "Publicar partido"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === "datos") {
    return (
      <div className="reservas-root rmob-root rmob-step-fixed">
        <Header title="Tus datos" subtitle={club.name} />
        <Progress />

        {holdExpiresAt && (
          <div className="rmob-countdown-bar">
            <HoldCountdown expiresAt={holdExpiresAt} onExpired={handleHoldExpiredLocal} />
          </div>
        )}

        <div className="rmob-step-body rmob-form-body">
          <div className="rmob-reserva-summary" style={{ padding: 0, overflow: "hidden" }}>
            <div className="sum-img" style={{ height: "120px", position: "relative" }}>
              <img src={img} alt={club.name} onError={(e) => { e.currentTarget.src = FALLBACK_IMAGE; }} />
              <div className="sc" />
              <div className="bn">{club.name}</div>
            </div>

            <div style={{ padding: "20px", display: "flex", flexDirection: "column", gap: "12px" }}>
              <h4 style={{ fontSize: "12px", fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase", color: "#6b7280", margin: "0 0 4px" }}>Resumen</h4>
              
              {selectedCourt && (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ display: "flex", alignItems: "center", gap: "8px", color: "#6b7280", fontWeight: 600, fontSize: "14px" }}>
                    <RIcon name="court" width={15} height={15} /> Cancha
                  </span>
                  <strong style={{ color: "#1f2937", fontSize: "14px", fontWeight: 700 }}>{selectedCourt.name}</strong>
                </div>
              )}
              
              {selectedDate && (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ display: "flex", alignItems: "center", gap: "8px", color: "#6b7280", fontWeight: 600, fontSize: "14px" }}>
                    <RIcon name="cal" width={15} height={15} /> Fecha
                  </span>
                  <strong style={{ color: "#1f2937", fontSize: "14px", fontWeight: 700 }}>{selectedDate.format("DD/MM/YYYY")}</strong>
                </div>
              )}
              
              {selectedStart && (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ display: "flex", alignItems: "center", gap: "8px", color: "#6b7280", fontWeight: 600, fontSize: "14px" }}>
                    <RIcon name="clock" width={15} height={15} /> Horario
                  </span>
                  <strong style={{ color: "#1f2937", fontSize: "14px", fontWeight: 700 }}>{selectedStart.slotStart} hs</strong>
                </div>
              )}
              
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ display: "flex", alignItems: "center", gap: "8px", color: "#6b7280", fontWeight: 600, fontSize: "14px" }}>
                  <RIcon name="clock" width={15} height={15} /> Duración
                </span>
                <strong style={{ color: "#1f2937", fontSize: "14px", fontWeight: 700 }}>{selectedDuration} min</strong>
              </div>

              {selectedCourt && (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <span style={{ display: "flex", alignItems: "center", gap: "8px", color: "#6b7280", fontWeight: 600, fontSize: "14px" }}>
                    <RIcon name="pin" width={15} height={15} /> Características
                  </span>
                  <strong style={{ color: "#1f2937", fontSize: "14px", fontWeight: 700, textAlign: "right", maxWidth: "60%" }}>
                    {[
                      SPORT_LABELS[selectedCourt.sport] || selectedCourt.sport,
                      SURFACE_LABELS[selectedCourt.surface] || selectedCourt.surface,
                      ENCLOSURE_LABELS[selectedCourt.enclosure] || selectedCourt.enclosure,
                      selectedCourt.lighting ? "Con iluminación" : "Sin iluminación",
                      selectedCourt.courtCategory,
                    ].filter(Boolean).join(" · ")}
                  </strong>
                </div>
              )}

              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <span style={{ display: "flex", alignItems: "center", gap: "8px", color: "#6b7280", fontWeight: 600, fontSize: "14px" }}>
                  <RIcon name="users" width={15} height={15} /> Jugadores
                </span>
                <PlayersCountField
                  value={playersCount}
                  onChange={setPlayersCount}
                  edited={playersEdited}
                  onEditedChange={setPlayersEdited}
                  defaultPlayers={defaultPlayers}
                  max={maxPlayers}
                  sportLabel={selectedCourt ? (SPORT_LABELS[selectedCourt.sport] || selectedCourt.sport) : ""}
                />
              </div>

              <div style={{ height: "1px", background: "#e5e7eb", margin: "8px 0" }} />

              {selectedStart?.price && (
                <>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ color: "#6b7280", fontWeight: 600, fontSize: "14px" }}>Precio de la cancha</span>
                    <strong style={{ color: "#1f2937", fontSize: "15px", fontWeight: 700 }}>{displayPrice}</strong>
                  </div>

                  <PerPlayerPrice
                    totalPrice={selectedStart.price}
                    players={playersCount}
                    variant="mobile"
                  />

                  {(() => {
                    const basePrice = Number(selectedStart.price);
                    const fakeFee = Math.round(basePrice * 0.01);
                    const nowPaying = resolveSlotDeposit(selectedStart, club);
                    return (
                      <>
                        <div style={{ fontSize: "13.5px", background: "var(--zoco-lime-tint, #F6F9E8)", padding: "8px 12px", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "space-between", margin: "4px 0" }}>
                          <span style={{ display: "flex", alignItems: "center", gap: "6px", color: "var(--zoco-lime-strong, #8FA80A)", fontWeight: 600 }}>
                            <RIcon name="ticket" width={14} height={14} /> Tasa de servicio
                          </span>
                          <span style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                            <span style={{ textDecoration: "line-through", color: "#9ca3af" }}>${fakeFee.toLocaleString("es-AR")}</span>
                            <strong style={{ color: "var(--zoco-lime-strong, #8FA80A)" }}>$0</strong>
                          </span>
                        </div>

                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", fontSize: "12px", color: "var(--zoco-lime-strong, #8FA80A)", fontWeight: 600, margin: "2px 0 6px" }}>
                          <RIcon name="shield" width={14} height={14} />
                          <span>Reserva segura, sin costos extra de servicio.</span>
                        </div>
                        
                        <div style={{ height: "1px", background: "#e5e7eb", margin: "8px 0" }} />

                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                          <span style={{ fontSize: "13px", fontWeight: 800, color: "#4b5563", textTransform: "uppercase", letterSpacing: "0.03em" }}>Ahora pagás</span>
                          <span style={{ color: "var(--zoco-lime-strong, #8FA80A)", fontWeight: 800, fontSize: "28px", lineHeight: "1" }}>
                            ${nowPaying.toLocaleString("es-AR")}
                          </span>
                        </div>
                        <div style={{ fontSize: "12px", fontStyle: "italic", color: "#6b7280", textAlign: "right", marginTop: "2px" }}>
                          {slotRequiresOnlinePayment(selectedStart, club) ? "Adelanto para reservar la cancha" : "Abonás el total en el club"}
                        </div>
                      </>
                    );
                  })()}
                </>
              )}
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmitLocal)} id="mob-reservation-form">
            <BuyerDataFields
              variant="mobile"
              register={register}
              errors={errors}
              trigger={trigger}
              country={selectedCountry}
              onCountryChange={setSelectedCountry}
              showNotes
              notesPlaceholder="Cumpleaños, de quién es la pala, preferencias…"
            />

            {!slotRequiresOnlinePayment(selectedStart, club) ? (
              <div style={{ padding: "12px 16px", background: "#f9fafb", border: "1px dashed #d1d5db", borderRadius: 8, fontSize: 13, color: "#4b5563", marginBottom: 16, lineHeight: 1.5 }}>
                <strong>Aclaración:</strong> No pagás nada online. El precio de la reserva lo abonarás presencialmente al momento del turno o mediante el método de pago elegido por el club (deberás estar atento ante una posible comunicación de parte del club).
              </div>
            ) : (
              <div style={{
                padding: "16px 20px",
                background: "#fffaf0",
                border: "1px solid #f97316",
                borderRadius: 8,
                fontSize: "13.5px",
                color: "#7c2d12",
                marginBottom: 16,
                lineHeight: 1.5,
                display: "flex",
                gap: "12px",
                alignItems: "flex-start"
              }}>
                <div style={{ color: "#ea580c", display: "flex", alignItems: "center", marginTop: "2px" }}>
                  <RIcon name="info" width={18} height={18} />
                </div>
                <div>
                  <strong style={{ color: "#9a3412", display: "block", marginBottom: "4px" }}>
                    Sólo se debitará el importe del anticipo/adelanto
                  </strong>
                  <span>
                    Abonarás luego el saldo restante de la reserva (si lo hubiera).
                    {club.cancellationAllowed !== false && club.cancellationAllowed !== "false" ? (
                      ` En caso de que canceles con ${club.cancellationPolicyHours || 24} hs de anticipación, se te reintegrará el importe automáticamente.`
                    ) : (
                      " Las reservas no son cancelables."
                    )}
                  </span>
                </div>
              </div>
            )}

            <MarketingOptIn venueName={club.name} checked={marketingOptIn} onChange={setMarketingOptIn} />

            <div className="consent" onClick={() => setConsent((c) => !c)}>
              <span className={`cbox${consent ? " on" : ""}`}>{consent && <RIcon name="check" width={13} height={13} />}</span>
              <p>Acepto la <strong>política de reservas</strong> de {club.name}{(club.cancellationAllowed !== false && club.cancellationAllowed !== "false") ? `: cancelación sin cargo hasta ${club.cancellationPolicyHours || 24} hs antes.` : ": las reservas no son cancelables."}</p>
            </div>

            {submitError && <Alert variant="danger" className="mt-3">{submitError}</Alert>}
          </form>

          <div style={{ height: 100 }} />
        </div>

        <div className="rmob-cta-bar">
          <SummaryPill />
          <div className="rmob-cta-actions">
            <button type="button" className="r-btn-soft rmob-back-btn" onClick={goBack}>
              <RIcon name="chevL" width={16} height={16} /> Volver
            </button>
            <button
              type="submit"
              form="mob-reservation-form"
              className="r-btn-primary rmob-cta-btn"
              disabled={submitting || !consent}
              style={{ opacity: consent ? 1 : 0.5 }}
            >
              {submitting
                ? <Spinner animation="border" size="sm" />
                : <>{slotRequiresOnlinePayment(selectedStart, club) ? "Ir al pago" : "Confirmar reserva"} <RIcon name="arrowR" width={18} height={18} /></>}
            </button>
          </div>
        </div>

        <Modal show={showPaymentModal} onHide={() => { setShowPaymentModal(false); setPendingValues(null); }} centered backdrop="static" className="payment-modal">
          <Modal.Header closeButton><Modal.Title>Elegí cómo pagar</Modal.Title></Modal.Header>
          <Modal.Body>
            <div className="mb-3">
              <label className="form-label">Tarjeta</label>
              <QtySelect className="payment-select" placeholder="Elegí una tarjeta" value={modalCard} onChange={(val) => setModalCard(val == null ? "" : String(val))} options={(cardOptions || []).map((opt) => ({ value: opt.id, label: opt.label, disabled: opt.disabled }))} />
            </div>
            {selectedCardOption && (
              <div className="mb-3">
                <label className="form-label">Cuotas</label>
                <QtySelect className="payment-select" placeholder="Elegí la cantidad de cuotas" value="1" disabled={true} onChange={() => {}} options={[{ value: "1", label: "1 cuota (sin interés)" }]} />
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => { setShowPaymentModal(false); setPendingValues(null); }}>Cancelar</Button>
            <Button variant="primary" onClick={handleConfirmPaymentSelection} disabled={!selectedCardOption}>Continuar</Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }

  return null;
}
